import * as API from '@siloreal:shared:api';

const COUNTRYSIDE_LIST_ENDPOINT = '/countrysides';

export type CountrysideListQueryValue = string | number | boolean | undefined | null;

export type CountrysideListQuery = Partial<{
  organization_id: CountrysideListQueryValue;
  write_permission: CountrysideListQueryValue;
}>;

export type CountrysideListServiceContract = {
  query?: CountrysideListQuery;
  signal?: AbortSignal;
};

export async function listCountrysidesService(
  contract: CountrysideListServiceContract = {}
): Promise<CountrysideListResponse> {
  const { data } = await API.http.fetch.get<CountrysideListApiResponse>(COUNTRYSIDE_LIST_ENDPOINT, {
    query: buildCountrysideListQuery(contract.query),
    signal: contract.signal,
  });

  return toCountrysideListResponse(data);
}

export function buildCountrysideListQuery(query?: CountrysideListQuery): string {
  const queryParameters = new URLSearchParams();

  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (typeof value === 'string' && value.trim().length === 0) return;

    queryParameters.set(key, String(value).trim());
  });

  const serializedQuery = queryParameters.toString();

  return serializedQuery ? `?${serializedQuery}` : '';
}

export type CountrysideListResponse = CountrysideListItem[];

type CountrysideListApiResponse = CountrysideListResponse | string;

export type CountrysideListItem = {
  id: number;
  name: string;
  active: boolean;
  created_at: Date | string;
  updated_at: Date | string | null;
  lat: string | null;
  lon: string | null;
  company_id: number;
  country_l2_id: number | null;
  companies: CountrysideListCompany;
  lots: CountrysideListLot[];
  countries_l2: CountrysideListCountryL2 | null;
  v_countrysides_list: CountrysideListPermission[];
  countryside_zone: CountrysideListZone | null;
};

export type CountrysideListCompany = {
  id: number;
  name: string;
  active: boolean;
  legal_name: string;
  tax_id: string;
  phone: string;
  email: string;
  organization_id: number;
  address_id: number | null;
  created_at: Date | string;
  updated_at: Date | string | null;
  organizations: CountrysideListOrganization;
};

export type CountrysideListOrganization = {
  id: number;
  name: string;
  active: boolean;
  legal_name: string;
  tax_id: string;
  phone: string;
  email: string;
  logo_url: string | null;
  poe_cycle: number | null;
  movement_notification_threshold: number;
  blockchain_enabled: boolean;
  prosegur_monitoring: boolean;
  agrotoken_integration: boolean;
  country_id: number;
  address_id: number | null;
  created_at: Date | string;
  updated_at: Date | string | null;
};

export type CountrysideListLot = {
  id: number;
  name: string;
  active: boolean;
  created_at: Date | string;
  updated_at: Date | string | null;
  countryside_id: number;
  country_l2_id: number | null;
};

export type CountrysideListCountryL2 = {
  id: number;
  name: string;
  created_at: Date | string;
  updated_at: Date | string | null;
  georef_id: bigint | number | string;
  division_type: 'Partido' | 'Municipio' | 'Departamento' | 'Comuna';
  country_l0_name: string;
  country_l1_name: string;
  country_l0_id: number;
  country_l1_id: number;
};

export type CountrysideListPermission = {
  user_id: number;
  countryside_id: number;
  read_permission: boolean;
  write_permission: boolean;
  full_permission: boolean;
  permission_id: number;
  is_guest: boolean;
  inherited: boolean;
  list_permission: boolean;
};

export type CountrysideListZone = {
  id: number;
  name: string;
  active: boolean;
  organization_id: number;
  created_at: Date | string;
  updated_at: Date | string | null;
};

function toCountrysideListResponse(data: CountrysideListApiResponse): CountrysideListResponse {
  const normalizedData = typeof data === 'string' ? JSON.parse(data) : data;

  return Array.isArray(normalizedData) ? (normalizedData as CountrysideListResponse) : [];
}
